import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { RefreshCcw } from "lucide-react";

export default function Refund() {
  const { lang } = useLang();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="overline text-secondary flex items-center gap-1.5"><RefreshCcw size={12} /> Refund & Cancellation</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "रिफंड और रद्दीकरण नीति" : "Refund & Cancellation Policy"}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {lang === "hi" ? "अंतिम अपडेट: फ़रवरी 2026" : "Last updated: February 2026"}
        </p>
      </div>

      <div className="space-y-6 text-sm leading-relaxed" data-testid="refund-content">
        <section>
          <h2 className="text-lg font-semibold text-primary">1. {lang === "hi" ? "प्लान्स और भुगतान" : "Plans & Payments"}</h2>
          <p className="text-muted-foreground mt-1">
            {lang === "hi"
              ? "BhoomiMitra पर लिस्टिंग बिल्कुल मुफ़्त है। प्रीमियम बूस्ट (₹299/माह या ₹999/वर्ष प्रति प्रॉपर्टी), Broker Plan (₹6999/वर्ष), और Builder Plan (₹7000/माह) वैकल्पिक हैं। सभी भुगतान Razorpay के सुरक्षित गेटवे से लिए जाते हैं।"
              : "Listing on BhoomiMitra is completely free. Premium Boost (₹299/mo or ₹999/yr per property), Broker Plan (₹6999/yr), and Builder Plan (₹7000/mo) are optional. All payments are processed securely through Razorpay."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">2. {lang === "hi" ? "रद्दीकरण" : "Cancellation"}</h2>
          <p className="text-muted-foreground mt-1">
            {lang === "hi"
              ? "आप किसी भी समय अपना प्लान रद्द कर सकते हैं। रद्द करने पर मौजूदा भुगतान अवधि की समाप्ति तक सुविधाएँ जारी रहेंगी। रद्दीकरण अनुरोध भेजने के लिए हमें nishuchoyal.jodhpur@gmail.com पर ईमेल करें या +91 70149 65172 पर WhatsApp करें।"
              : "You may cancel your plan at any time. Upon cancellation, benefits remain active until the end of the current billing period. To cancel, email nishuchoyal.jodhpur@gmail.com or WhatsApp +91 70149 65172."}
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">3. {lang === "hi" ? "रिफंड नीति" : "Refund Policy"}</h2>
          <p className="text-muted-foreground mt-1">
            {lang === "hi" ? "रिफंड निम्नलिखित परिस्थितियों में जारी किए जाते हैं:" : "Refunds are issued under the following circumstances:"}
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>
              <strong>{lang === "hi" ? "डुप्लिकेट भुगतान:" : "Duplicate payment:"}</strong>{" "}
              {lang === "hi" ? "अगर एक ही प्लान/लिस्टिंग के लिए दो बार शुल्क कट गया — 100% रिफंड, 5-7 कार्य दिवसों में।" : "If you were charged twice for the same plan/listing — 100% refund within 5-7 business days."}
            </li>
            <li>
              <strong>{lang === "hi" ? "तकनीकी विफलता:" : "Technical failure:"}</strong>{" "}
              {lang === "hi" ? "अगर भुगतान होने के बावजूद सेवा सक्रिय नहीं हुई और 48 घंटे में समाधान नहीं हुआ — 100% रिफंड।" : "If payment was deducted but the service was not activated within 48 hours — 100% refund."}
            </li>
            <li>
              <strong>{lang === "hi" ? "पहले 24 घंटे में रद्दीकरण:" : "Cancellation within 24 hours:"}</strong>{" "}
              {lang === "hi" ? "नए प्लान की खरीद के 24 घंटे के भीतर रद्द करने पर पूरा रिफंड — बशर्ते प्लान की सुविधाओं का इस्तेमाल न किया हो।" : "Full refund if you cancel within 24 hours of a new plan purchase — provided plan benefits have not been used."}
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">4. {lang === "hi" ? "गैर-रिफंडेबल परिस्थितियाँ" : "Non-refundable scenarios"}</h2>
          <ul className="list-disc pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>{lang === "hi" ? "प्लान की समाप्ति के बाद (जैसे 30-दिन का Premium Boost समाप्त होने पर)" : "After the plan has expired (e.g., a 30-day Premium Boost that has run its course)"}</li>
            <li>{lang === "hi" ? "अगर लिस्टिंग को उपयोगकर्ता द्वारा नियमों का उल्लंघन करने के कारण हटा दिया गया हो" : "If a listing was removed for violating our Terms & Conditions"}</li>
            <li>{lang === "hi" ? "आंशिक उपयोग के बाद (उदाहरण: 5 दिन उपयोग के बाद रद्द करने पर शेष दिनों का रिफंड नहीं मिलेगा)" : "Partial usage (e.g., cancelling a monthly plan after 5 days of active use)"}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">5. {lang === "hi" ? "रिफंड कैसे प्राप्त करें" : "How to request a refund"}</h2>
          <ol className="list-decimal pl-5 space-y-1 mt-2 text-muted-foreground">
            <li>{lang === "hi" ? "अपनी Order ID तैयार रखें (My Listings/Plans page पर मिलेगी)" : "Keep your Order ID ready (visible on My Listings/Plans page)"}</li>
            <li>{lang === "hi" ? "ईमेल करें nishuchoyal.jodhpur@gmail.com पर, विषय में \"Refund Request — <Order ID>\" लिखें" : 'Email nishuchoyal.jodhpur@gmail.com with subject "Refund Request — <Order ID>"'}</li>
            <li>{lang === "hi" ? "हम 48 घंटे के भीतर जवाब देंगे और स्वीकृति के बाद 5-7 कार्य दिवसों में रिफंड आपके मूल भुगतान माध्यम पर वापस आ जाएगा" : "We respond within 48 hours; upon approval the refund reaches your original payment method within 5-7 business days"}</li>
          </ol>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary">6. {lang === "hi" ? "संपर्क" : "Contact"}</h2>
          <p className="text-muted-foreground mt-1">
            {lang === "hi" ? "किसी भी प्रश्न के लिए:" : "For any questions:"}
          </p>
          <ul className="list-none pl-0 mt-1 text-muted-foreground space-y-0.5">
            <li>📧 <a href="mailto:nishuchoyal.jodhpur@gmail.com" className="text-primary hover:underline">nishuchoyal.jodhpur@gmail.com</a></li>
            <li>📞 <a href="tel:+917014965172" className="text-primary hover:underline">+91 70149 65172</a></li>
            <li>💬 <a href="https://wa.me/917014965172" target="_blank" rel="noreferrer" className="text-primary hover:underline">WhatsApp</a></li>
          </ul>
        </section>

        <div className="pt-6 border-t border-border text-xs text-muted-foreground">
          {lang === "hi" ? (
            <>यह भी देखें: <Link to="/terms" className="text-primary hover:underline">नियम</Link> • <Link to="/privacy" className="text-primary hover:underline">गोपनीयता नीति</Link></>
          ) : (
            <>See also: <Link to="/terms" className="text-primary hover:underline">Terms</Link> • <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link></>
          )}
        </div>
      </div>
    </div>
  );
}
